import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-multi-date-picker";

import { timezoneOptions, paginationConstants } from "../../../constants";
import { getFilterExpression } from "../../../shared";

const pageSizeOptions = [5, 10, 20, 30];

const PaginationComponent = ({
  pagination,
  setPagination,
  searchText,
  setSearchText,
  date,
  setDate,
  listDateRange = false,
  ...props
}) => {
  const { t } = useTranslation("common");
  const { pageSize, pageNumber, totalRows } = pagination;
  const {
    timezone,
    setTimezone,
    filterOptionValue,
    setFilterOptionValue,
    isFilterKeyValuePair,
  } = props;

  const isStatus = !!props?.status;

  useEffect(() => {
    if (!timezone && setTimezone) {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const isValid = timezoneOptions.some(
        (tz) => tz.value === browserTimezone,
      );
      if (isValid) {
        setTimezone(browserTimezone);
      }
    }
  }, []);

  const handleSearch = (event) => {
    setSearchText(event.target.value);
    if (!listDateRange) {
      setDate && setDate();
    }
  };

  const handleDateSelect = (event) => {
    setDate(event);
    if (!listDateRange) {
      setSearchText();
    }
  };

  const handleFilterOptionValue = (event) => {
    setFilterOptionValue(event.target.value);
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    handlePageChange(1);
  };

  // ----- Handlers -----
  const handlePageChange = (page, size = null) => {
    let filter = null;
    if (isStatus) {
      filter = props.status;
    } else if (listDateRange) {
      filter = getFilterExpression(
        paginationConstants.PAGINATION_FILTER_TYPE.LIST_DATE_RANGE,
        searchText,
        filterOptionValue,
        date,
      );
    } else if (date) {
      filter = getFilterExpression(
        paginationConstants.PAGINATION_FILTER_TYPE.DATE_RANGE,
        date,
        filterOptionValue,
        [],
        props.filterOptions,
        timezone,
      );
    } else {
      filter = getFilterExpression(
        paginationConstants.PAGINATION_FILTER_TYPE.CONTAINS,
        searchText,
        filterOptionValue,
        [],
        props.filterOptions,
      );
    }
    props.getTableData(size ? size : pageSize, page, filter);
  };

  const handlepageSizeChange = (event) => {
    const perPage = parseInt(event.target.value, 10);
    handlePageChange(1, perPage);
  };

  return (
    <div
      className={`back-color pagination-con ${
        props.isTable ? "p-lr-8" : "p-lr-22"
      }`}
    >
      {totalRows > 0 && (
        <div className="d-flex flex-column flex-md-row justify-content-between text-center text-md-left mt-3 p-2">
          <div className="pagination-controls">
            <span className="showing-info">
              {t("showing")} {pageNumber * pageSize - pageSize + 1} {t("to")}{" "}
              {Math.min(pageNumber * pageSize, totalRows)} of {totalRows}
            </span>
          </div>
          <div className="rows-per-page">
            <button
              type="button"
              className="border-0 bg-transparent"
              onClick={() => handlePageChange(1)}
              disabled={pageNumber === 1}
            >
              <i className="fas fa-angle-double-left"></i>
            </button>
            <button
              type="button"
              className="border-0 bg-transparent"
              onClick={() => handlePageChange(pageNumber - 1)}
              disabled={pageNumber === 1}
            >
              <i className="fas fa-angle-left"></i>
            </button>
            <span className="current-page">
              {t("page")} {pageNumber} {t("of")}{" "}
              {Math.ceil(totalRows / pageSize)}
            </span>
            <button
              type="button"
              className="border-0 bg-transparent"
              onClick={() => handlePageChange(pageNumber + 1)}
              disabled={
                pageNumber === Math.ceil(totalRows / pageSize) ||
                totalRows === 0
              }
            >
              <i className="fas fa-angle-right"></i>
            </button>
            <button
              type="button"
              className="border-0 bg-transparent"
              onClick={() => handlePageChange(Math.ceil(totalRows / pageSize))}
              disabled={
                pageNumber === Math.ceil(totalRows / pageSize) ||
                totalRows === 0
              }
            >
              <i className="fas fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      )}
      <div className="d-flex flex-column flex-xl-row justify-content-between p-2">
        <div className="rows-per-page d-flex col-12 col-xl-4 p-0 mb-2 mb-md-0">
          <span className="mr-2 mt-1">{t("show")}</span>
          <select
            className="form-control mb-1 drop-down-style"
            value={pageSize}
            onChange={handlepageSizeChange}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <span className="ml-2 mt-1">{t("entries")}</span>
        </div>

        {props.filterOptions && (
          <form
            className="rows-per-page d-flex col-12 col-xl-8 p-0 mb-2 mb-md-0"
            onSubmit={handleFilterSubmit}
          >
            <select
              className="form-control mr-1 w-100 h-100 drop-down-style"
              onChange={handleFilterOptionValue}
            >
              <option selected disabled value="" key={0}>
                Select Filter
              </option>
              {isFilterKeyValuePair
                ? props.filterOptions.map((obj) => {
                    return filterOptionValue === obj.Selector ? (
                      <option key={obj.Selector} value={obj.Selector} selected>
                        {obj.Name}
                      </option>
                    ) : (
                      <option key={obj.Selector} value={obj.Selector}>
                        {obj.Name}
                      </option>
                    );
                  })
                : props.filterOptions.map((option) => {
                    return filterOptionValue === option ? (
                      <option key={option} value={option} selected>
                        {option}
                      </option>
                    ) : (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    );
                  })}
            </select>

            {props.isDateIncluded &&
            (filterOptionValue.includes("date") ||
              filterOptionValue.includes("Date")) ? (
              <div className="form-control search">
                <DatePicker
                  value={date}
                  onChange={(e) => {
                    handleDateSelect(e);
                  }}
                  zIndex={9999}
                  containerClassName="mui-date-picker-popup"
                  required
                />
              </div>
            ) : props.listDateRange && filterOptionValue ? (
              <DatePicker
                value={date}
                range={true}
                onChange={(e) => {
                  handleDateSelect(e);
                }}
                zIndex={9999}
                containerClassName="mui-date-picker-popup"
              />
            ) : (
              filterOptionValue && (
                <input
                  type="text"
                  placeholder="Search..."
                  className="form-control search"
                  style={{ height: "35px" }}
                  maxLength={50}
                  value={searchText}
                  onChange={handleSearch}
                />
              )
            )}
            {listDateRange && (
              <DatePicker
                value={date}
                range={true}
                rangeHover={true}
                highlightToday={false}
                style={{ marginLeft: "1%", marginRight: "2%", height: "34px" }}
                onChange={(e) => {
                  handleDateSelect(e);
                }}
                zIndex={9999}
              />
            )}

            {/**** Timezone select for date filters ****/}
            {filterOptionValue && /date/i.test(filterOptionValue) && (
              <select
                required
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="form-control mr-1 w-100 h-100 drop-down-style"
              >
                <option disabled value="">
                  Select Timezone
                </option>
                {timezoneOptions.map((opt) => {
                  return (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  );
                })}
              </select>
            )}
            <button className="pagination-search-btn">Search</button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PaginationComponent;
